// firstRun.ts · v12.10.30 (#2256 checklist steps for a practice feature that is off are hidden; Call back named only when it is on) · 2026-11-03 · VoiceSpectrum AI
// firstRun.ts · v12.10.25.1 (#2106 Tushar 10/30: checklist Show me opens a message for in-message steps; Get familiar - filters area, the message cards, then the drawer: the call, what the caller needs, transcript, call data, mark complete / print / copy)
// firstRun.ts · v12.10.25 (#2106 first-time user - Welcome, Help guides, tours and checklists by rights) · 2026-10-30 · VoiceSpectrum AI
// LOCKED prototype: Documents\Scope\Work In Progress\UI-PROTO-FIRST-RUN-T180_v1.4.html (Tushar 10/30).
//  - The guides (Help landing rows): Get familiar · Daily work · Reports · Configuration · Workflow · Security. A guide shows
//    only when the person's rights open at least one of its screens; a step for a screen they cannot open is skipped.
//  - Each guide: a spotlight tour on the real screens (Play guide) and a checklist (the "Let's get started · n/N" pill)
//    whose steps tick themselves from what the person really does - the pill then moves to the next step by itself.
//  - Progress is kept per person on the server (GET / PUT /api/t2/security/account/first-run); nothing here is patient data.
// The tick listeners read the Click Analyzer's data-track names (lib/uiTrack.ts contract), audio play events and the route.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { securityApi, type FirstRunStateName, type FirstRunView } from './securityApi';
import { cachedFeatureOn, type PracticeFeatureKey } from './practiceFeatures';   // v12.10.30 (#2256)

export const FIRST_RUN_KEY = ['t2-first-run'];
export type GuideKey = 'fam' | 'daily' | 'rep' | 'conf' | 'wf' | 'sec';
/** msg = Messages or Texts · con = Contacts · rep = Reports › Call activity · set = any Settings row but Workflow ·
 *  wf = Settings › Workflow · sec = Security (users, groups, platform). null = everyone. */
export type RightKey = 'msg' | 'con' | 'rep' | 'set' | 'wf' | 'sec' | null;

export interface TourStep {
  title: string;
  text: string;
  /** where the step is shown (navigated to first); none = wherever the person is */
  route?: string;
  /** CSS selector of the thing to spotlight; none or not found = a centred card */
  target?: string;
  /** also pulse this (e.g. the « collapse button in step 1) */
  pulse?: string;
  right: RightKey;
  prep?: 'openRow' | 'closeDrawer' | 'openMenu' | 'closeMenu';
}
export interface CheckItem { key: string; label: string; how: string; right: RightKey; route?: string; target?: string; inDrawer?: boolean;
  /** v12.10.30 (#2256): hidden while this practice feature is off. */
  feature?: PracticeFeatureKey;
  /** v12.10.30 (#2256): while this feature is off the step reads differently (it stays - only the part that needs the feature goes). */
  whenOff?: { feature: PracticeFeatureKey; label: string; how: string; target?: string } }   // v12.10.25.1: inDrawer = Show me opens a message first
export interface Guide { key: GuideKey; icon: string; name: string; len: string; desc: string; rights: RightKey[]; tour: TourStep[]; check: CheckItem[] }

const NAV = (label: string) => `.sidebar [data-tip="${label}"]`;
const ROW = '[data-track="messages.row.open"]';

export const GUIDES: Guide[] = [
  {
    key: 'fam', icon: '▶', name: 'Get familiar', len: '2 min', desc: 'The basics everyone needs - your inbox, a call, and your account.', rights: [null],
    tour: [
      { route: '/messages', prep: 'closeDrawer', target: '.aw-topline', pulse: '.sidebar .sb-collapse', right: 'msg', title: 'Messages is your inbox',
        text: 'Every call the AI answered lands here. Up next, the pills, the dates and Filters pick which calls you see. The menu on the left takes you everywhere; collapse it with « for more room, and « again brings it back.' },
      { route: '/messages', prep: 'closeDrawer', target: '.aw-bucket', right: 'msg', title: 'The message cards',
        text: 'One card per call, newest first - a blue dot means nobody has opened it yet. Click a card to open the call.' },
      { route: '/messages', prep: 'openRow', target: '.aw-drawer-identity', right: 'msg', title: 'The call',
        text: 'The top of the drawer: who called, their number, the language, the reference number and when - plus the status and who it is assigned to.' },
      { route: '/messages', prep: 'openRow', target: '.aw-sum-grid', right: 'msg', title: 'What the caller needs',
        text: 'What they want, the number to call back and the next step. Read this first - then Play to hear the call.' },
      { route: '/messages', prep: 'openRow', target: '[data-track="drawer.section.transcript"]', right: 'msg', title: 'Transcript',
        text: 'Every word of the call, the agent and the caller, with the time of each line.' },
      { route: '/messages', prep: 'openRow', target: '[data-track="drawer.section.calldata"]', right: 'msg', title: 'Call data',
        text: 'Each detail the agent captured - name, date of birth, request - and which ones are flagged for you to check.' },
      { route: '/messages', prep: 'openRow', target: '.aw-drawer-footer-right', right: 'msg', title: 'Mark complete, print, copy',
        text: 'When it is handled, Mark complete (Ctrl+Enter) - it leaves your open list. Print ▾ prints the call or copies the whole call record.' },
      { prep: 'closeDrawer', target: NAV('Contacts'), right: 'con', title: 'Contacts',
        text: 'Everyone who has called - patients, offices, pharmacies - with their numbers and their calls.' },
      { prep: 'closeMenu', target: '.sidebar .sb-card-trigger', right: null, title: 'Your account',
        text: 'Click your name for your account - your profile, your password and your 2-step sign-in.' },
      { prep: 'openMenu', target: '.profile-menu', right: null, title: 'Preferences',
        text: 'Your account › Preferences: your notifications and which follow-ups the left-menu badge counts.' },
      { prep: 'openMenu', target: '.profile-menu .pm-signout', right: null, title: 'Sign out - and Help',
        text: 'Sign out here. Help on the left brings back any guide, and the Help library has every article for your screens.' },
    ],
    check: [
      { key: 'open', label: 'Open a message', how: 'Click a row in Messages', right: 'msg', route: '/messages', target: ROW },
      { key: 'play', label: 'Play a recording', how: 'Open a message › Play', right: 'msg', route: '/messages', target: '.aw-drawer-footer', inDrawer: true },
      { key: 'back', label: 'Call back or send a text', how: 'Call back / Send text in a message', right: 'msg', route: '/messages', target: '[data-track="drawer.action.callback"]', inDrawer: true,
        whenOff: { feature: 'call_back', label: 'Send a text', how: 'Send text in a message', target: '[data-track="drawer.action.text"]' } },   // v12.10.30 (#2256): Call back named only when on
      { key: 'done', label: 'Mark one complete', how: 'Mark complete at the bottom of a message', right: 'msg', route: '/messages', target: '[data-track="drawer.action.complete"]', inDrawer: true },
      { key: 'rem', label: 'Set a follow-up', how: 'Open a message › Follow-up reminder', right: 'msg', route: '/messages', target: '[data-track="drawer.action.reminder"]', inDrawer: true },
      { key: 'search', label: 'Search for a caller', how: 'The search box on Messages', right: 'msg', route: '/messages', target: '.aw-search' },
      { key: 'con', label: 'Open a contact', how: 'Contacts on the left', right: 'con', target: NAV('Contacts') },
      { key: 'tour', label: 'Take the 2-minute tour', how: 'Help › Get familiar › Play guide', right: null, target: '.sidebar a[href="/guides"]' },
      { key: 'hours', label: 'Check your office hours', how: 'Settings › Organization › Hours', right: 'set', route: '/configuration/organization' },
      { key: 'voice', label: 'Listen to the agent\'s voice', how: 'Settings › AI Agent › Agent details › Play', right: 'set', route: '/configuration/assistant', feature: 'ai' },   // v12.10.30 (#2256)
      { key: 'ch', label: 'Review Call Handling', how: 'Settings › Call Handling', right: 'set', route: '/configuration/call-handling' },
      { key: 'user', label: 'Add a user', how: 'Settings › Security › Users › Invite', right: 'sec', route: '/configuration/security?page=users' },
    ],
  },
  {
    key: 'daily', icon: '✉', name: 'Daily work', len: '4 min', desc: 'Message cards, the message drawer and Contacts in depth.', rights: ['msg', 'con'],
    tour: [
      { route: '/messages', prep: 'closeDrawer', target: '[data-track="messages.filters.open"]', right: 'msg', title: 'Filters and dates',
        text: 'Narrow the list by unread, request type, status or dates. The counters follow the dates and filters you pick.' },
      { route: '/messages', target: '.aw-search', right: 'msg', title: 'Search everywhere',
        text: 'Search a name, phone or call number. It looks in the dates on screen first; when there is nothing there, the button shows matches in other dates.' },
      { route: '/messages', prep: 'openRow', target: '.aw-drawer-identity', right: 'msg', title: 'Groups and Combined',
        text: 'Calls from the same caller are grouped. Open the group and use Combined to read every call as one.' },
      { route: '/messages', prep: 'openRow', target: '[data-track="drawer.action.reminder"]', right: 'msg', title: 'Follow-up reminders',
        text: 'Set a reminder so the call comes back on the right day - Follow-ups on the left counts them.' },
      { route: '/messages', prep: 'openRow', target: '[data-track="drawer.action.assign"]', right: 'msg', title: 'Assign, labels, urgent',
        text: 'Assign the call to a colleague, add a label or flag it urgent. Print or copy the record from the same place.' },
      { route: '/contacts', prep: 'closeDrawer', target: '.tenant-main', right: 'con', title: 'Contacts in depth',
        text: 'Search, edit, merge two contacts into one, text consent, and Caller / Relation for family and caregivers. The gear holds Contact book settings.' },
    ],
    check: [
      { key: 'f', label: 'Use a filter', how: 'Messages › Filters', right: 'msg', route: '/messages', target: '[data-track="messages.filters.open"]' },
      { key: 's', label: 'Search everywhere', how: 'Messages › search box', right: 'msg', route: '/messages', target: '.aw-search' },
      { key: 'g', label: 'Open a group', how: 'A grouped row › Combined', right: 'msg', route: '/messages', target: '[data-track="drawer.group.combined"]', inDrawer: true },
      { key: 'r', label: 'Set a follow-up', how: 'Message › Follow-up reminder', right: 'msg', route: '/messages', target: '[data-track="drawer.action.reminder"]', inDrawer: true },
      { key: 'm', label: 'Merge two contacts', how: 'Contacts › a contact › ⋮ › Merge into', right: 'con', route: '/contacts' },
    ],
  },
  {
    key: 'rep', icon: '▤', name: 'Reports', len: '2 min', desc: 'Call activity - filters and sharing.', rights: ['rep'],
    tour: [
      { route: '/reports', prep: 'closeDrawer', target: '.tenant-main', right: 'rep', title: 'Call activity',
        text: 'Calls by day, type and outcome. Pick the dates and filters, then share the report with a colleague.' },
    ],
    check: [
      { key: 'o', label: 'Open Call activity', how: 'Reports on the left', right: 'rep', route: '/reports' },
      { key: 'sh', label: 'Share a report', how: 'Reports › Share', right: 'rep', route: '/reports' },
    ],
  },
  {
    key: 'conf', icon: '⚙', name: 'Configuration', len: '5 min', desc: 'Practice setup and your words - hours, voices, languages, Call Handling, Personalization.', rights: ['set'],
    tour: [
      { route: '/configuration/organization', prep: 'closeDrawer', target: '.tenant-main', right: 'set', title: 'Practice setup',
        text: 'Your practice name, office hours and holidays - the agent uses them on every call.' },
      { route: '/configuration/assistant', target: '.tenant-main', right: 'set', title: 'Voices, languages, handoff',
        text: 'The agent\'s voice and languages, voicemail, and who a caller reaches when they ask for a person.' },
      { route: '/configuration/call-handling', target: '.tenant-main', right: 'set', title: 'Call Handling and Keypad menus',
        text: 'How the agent listens and waits, recording, and the keys every keypad menu uses.' },
      { route: '/configuration/personalization', target: '.tenant-main', right: 'set', title: 'Your words',
        text: 'Caller types, categories, labels and repeat callers - and in Vocabulary your fields and lists. Language proofing checks every line in a language.' },
    ],
    check: [
      { key: 'h', label: 'Check office hours', how: 'Settings › Organization › Hours', right: 'set', route: '/configuration/organization' },
      { key: 'v', label: 'Play the agent\'s voice', how: 'Settings › AI Agent › Agent details › Play', right: 'set', route: '/configuration/assistant', feature: 'ai' },   // v12.10.30 (#2256)
      { key: 'k', label: 'Open Keypad menus', how: 'Settings › Call Handling › Keypad menus', right: 'set', route: '/configuration/call-handling' },
    ],
  },
  {
    key: 'wf', icon: '⇄', name: 'Workflow', len: '4 min', desc: 'Cards, wording and the simulator.', rights: ['wf'],
    tour: [
      { route: '/configuration/workflow', prep: 'closeDrawer', target: '.tenant-main', right: 'wf', title: 'Workflow',
        text: 'The cards a call goes through and their wording. Open a card to edit it, and use the Runtime simulator to try a call before it goes live.' },
    ],
    check: [
      { key: 'c', label: 'Open a card', how: 'Settings › Workflow › a card', right: 'wf', route: '/configuration/workflow' },
      { key: 'sim', label: 'Run the simulator', how: 'Workflow › Runtime simulator', right: 'wf', route: '/configuration/workflow' },
    ],
  },
  {
    key: 'sec', icon: '🔒', name: 'Security', len: '3 min', desc: 'Users, user groups and rights, sign-in and the audit log.', rights: ['sec'],
    tour: [
      { route: '/configuration/security?page=users', prep: 'closeDrawer', target: '.tenant-main', right: 'sec', title: 'Users',
        text: 'Invite people, resend an invite, reset a password or 2-step, sign someone out, or disable them. Show welcome again brings these guides back for a person.' },
      { route: '/configuration/security?page=groups', target: '.tenant-main', right: 'sec', title: 'User groups and rights',
        text: 'What each group can open - section by section, None / View / Work / Full. People get the rights of their groups.' },
      { route: '/configuration/security?page=platform', target: '.tenant-main', right: 'sec', title: 'Sign-in, sessions and audit',
        text: 'Password rules, 2-step, how long a session lasts - and every security change is written to the audit log.' },
    ],
    check: [
      { key: 'u', label: 'Invite a user', how: 'Security › Users › Invite', right: 'sec', route: '/configuration/security?page=users' },
      { key: 'g', label: 'Look at a group\'s rights', how: 'Security › User groups', right: 'sec', route: '/configuration/security?page=groups' },
    ],
  },
];

export const guideByKey = (k: string | null | undefined): Guide | undefined => GUIDES.find((g) => g.key === k);

/** The person's rights in guide words (unknown access = everything shown; the server enforces anyway). */
export function rightsFrom(access: Record<string, string[] | undefined> | null | undefined): (r: RightKey) => boolean {
  if (!access) return () => true;
  const has = (k: string) => (access[k] ?? []).length > 0;
  const keys = Object.keys(access);
  return (r: RightKey) => {
    switch (r) {
      case null: return true;
      case 'msg': return has('phone') || has('texts');
      case 'con': return has('contacts');
      case 'rep': return has('rep_calls');
      case 'wf': return has('s:Workflow:Workflow');
      case 'set': return keys.some((k) => k.startsWith('s:') && k !== 's:Workflow:Workflow' && has(k));
      case 'sec': return has('sec_users') || has('sec_roles') || has('sec_platform');
      default: return false;
    }
  };
}

export const visibleGuides = (can: (r: RightKey) => boolean) => GUIDES.filter((g) => g.rights.some((r) => can(r)));
export const tourOf = (g: Guide, can: (r: RightKey) => boolean) => g.tour.filter((s) => can(s.right));
// v12.10.30 (#2256, Tushar 11/3 ~5:55 PM: "#the help topic of disabled feature will also be not available."): a step for a feature that is off is not offered; a step with whenOff reads its other way.
export const checkOf = (g: Guide, can: (r: RightKey) => boolean) => g.check
  .filter((c) => can(c.right) && (!c.feature || cachedFeatureOn(c.feature)))
  .map((c) => (c.whenOff && !cachedFeatureOn(c.whenOff.feature)
    ? { ...c, label: c.whenOff.label, how: c.whenOff.how, target: c.whenOff.target ?? c.target }
    : c));

// ── which real action ticks which step (data-track names from lib/uiTrack.ts, routes, audio play) ─────────────
export type Tick = [GuideKey, string];
export function ticksForTrack(name: string): Tick[] {
  switch (name) {
    case 'messages.row.open': case 'messages.row.arrow': return [['fam', 'open']];
    case 'drawer.action.callback': case 'drawer.action.text': return [['fam', 'back']];
    case 'drawer.action.complete': return [['fam', 'done']];
    case 'drawer.action.reminder': return [['fam', 'rem'], ['daily', 'r']];
    case 'messages.filters.open': return [['daily', 'f']];
    case 'drawer.group.combined': case 'drawer.group.call': case 'drawer.group.play': return [['daily', 'g']];
    default: return [];
  }
}
export function ticksForRoute(pathname: string, search: string): Tick[] {
  const p = pathname.toLowerCase();
  const page = new URLSearchParams(search).get('page');
  if (p.startsWith('/contacts')) return [['fam', 'con']];
  if (p.startsWith('/reports')) return [['rep', 'o']];
  if (p.startsWith('/configuration/organization')) return [['fam', 'hours'], ['conf', 'h']];
  if (p.startsWith('/configuration/call-handling')) return [['fam', 'ch']];
  if (p.startsWith('/workflows/')) return [['wf', 'c']];
  if (p.startsWith('/configuration/security') && page === 'groups') return [['sec', 'g']];
  return [];
}
/** A click on a button whose words match - for screens without data-track names (Contacts, Reports, Settings). */
export function ticksForButtonText(pathname: string, search: string, text: string): Tick[] {
  const p = pathname.toLowerCase(); const t = text.trim().toLowerCase();
  const page = new URLSearchParams(search).get('page');
  if (!t) return [];
  if (p.startsWith('/contacts') && t.startsWith('merge into')) return [['daily', 'm']];
  if (p.startsWith('/reports') && t.startsWith('share')) return [['rep', 'sh']];
  if (p.startsWith('/configuration/call-handling') && t.startsWith('keypad menus')) return [['conf', 'k']];
  if ((p.startsWith('/workflows') || p.startsWith('/configuration/workflow')) && t.includes('simulator')) return [['wf', 'sim']];
  if (p.startsWith('/configuration/security') && (page === 'users' || !page) && t.startsWith('invite')) return [['fam', 'user'], ['sec', 'u']];
  return [];
}
export function ticksForPlay(pathname: string): Tick[] {
  const p = pathname.toLowerCase();
  if (p.startsWith('/messages')) return [['fam', 'play']];
  if (p.startsWith('/configuration/assistant')) return [['fam', 'voice'], ['conf', 'v']];
  return [];
}
/** The search box on Messages was typed in. */
export const ticksForSearch = (): Tick[] => [['fam', 'search'], ['daily', 's']];

// ── the bus: Shell's host listens; Help, the name menu and the Welcome send ─────────────────────────────────────
export type FirstRunCommand =
  | { action: 'tour'; guide: GuideKey }
  | { action: 'checklist'; guide: GuideKey }
  | { action: 'welcome' };
export const FIRST_RUN_EVENT = 'vs:first-run';
/** Shell listens for this to open / close the profile menu during the Get familiar tour (steps 6-7). */
export const FIRST_RUN_MENU_EVENT = 'vs:first-run-menu';
export function sendFirstRun(cmd: FirstRunCommand): void {
  try { window.dispatchEvent(new CustomEvent<FirstRunCommand>(FIRST_RUN_EVENT, { detail: cmd })); } catch { /* old browsers */ }
}
/** Help opens in its own tab - a command from there is parked here and run by the app tab's host on its next load. */
export const PENDING_KEY = 'vs.firstRun.pending';
export function parkFirstRun(cmd: FirstRunCommand): void {
  try { sessionStorage.setItem(PENDING_KEY, JSON.stringify(cmd)); } catch { /* private window */ }
}
export function takeParked(): FirstRunCommand | null {
  try { const v = sessionStorage.getItem(PENDING_KEY); sessionStorage.removeItem(PENDING_KEY); return v ? (JSON.parse(v) as FirstRunCommand) : null; } catch { return null; }
}

/** The person's first-run state + progress (React Query; a Support visit gets show=false and no progress). */
export function useFirstRun() {
  const qc = useQueryClient();
  const q = useQuery<FirstRunView>({ queryKey: FIRST_RUN_KEY, queryFn: () => securityApi.firstRun(), staleTime: 60_000, retry: false });
  const save = useCallback(async (body: { state?: FirstRunStateName; guide?: string; done?: string[] }) => {
    // optimistic: the pill and the Help rows move at once; the server's answer replaces it
    qc.setQueryData<FirstRunView>(FIRST_RUN_KEY, (old) => {
      if (!old) return old;
      const progress = { ...old.progress };
      if (body.guide && body.done?.length) progress[body.guide] = Array.from(new Set([...(progress[body.guide] ?? []), ...body.done]));
      return { ...old, state: body.state ?? old.state, show: body.state ? false : old.show, progress };
    });
    try {
      const r = await securityApi.saveFirstRun(body);
      qc.setQueryData<FirstRunView>(FIRST_RUN_KEY, (old) => (old ? { ...old, state: r.state, progress: r.progress } : old));
    } catch { /* kept locally until the next load - a support visit or an old API cannot save */ }
  }, [qc]);
  return { data: q.data ?? null, loaded: q.isSuccess, save };
}
